import { Controller } from "@hotwired/stimulus";

// Handles browser Web Push subscription opt-in/opt-out.
//
// Data values expected on the controller element:
//   data-push-notification-vapid-key-value            — base64url VAPID public key
//   data-push-notification-subscribe-url-value         — API endpoint (POST + DELETE)
//   data-push-notification-unsupported-msg-value       — shown when the browser lacks push support
//   data-push-notification-ios-msg-value               — shown for iOS Safari (add-to-home-screen hint)
//   data-push-notification-subscribe-failed-msg-value  — shown when PushManager.subscribe throws
//   data-push-notification-save-failed-msg-value        — shown when the server rejects the subscription
//
// Targets:
//   button  — the subscribe/unsubscribe toggle button
//   label   — text node inside the button (updated on state change)
//   status  — small paragraph for error / hint messages (hidden by default)

const SW_PATH = "/service-worker.js";

export default class extends Controller {
  static targets = ["button", "label", "status"];
  static values = {
    vapidKey: String,
    subscribeUrl: String,
    unsupportedMsg: String,
    iosMsg: String,
    subscribeFailedMsg: String,
    saveFailedMsg: String,
  };

  async connect() {
    if (!this.#supported()) {
      this.#setUnsupported(this.unsupportedMsgValue);
      return;
    }
    if (this.#isIosInBrowser()) {
      this.#setUnsupported(this.iosMsgValue);
      return;
    }

    await this.#registerSW();
    await this.#syncState();
  }

  async toggle() {
    const registration = await this.#swRegistration();
    if (!registration) return;

    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await this.#unsubscribe(existing);
    } else {
      await this.#subscribe(registration);
    }
  }

  // ── private ──────────────────────────────────────────────────────────────

  #supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  }

  #isIosInBrowser() {
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const standalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;
    return ios && !standalone;
  }

  async #registerSW() {
    try {
      await navigator.serviceWorker.register(SW_PATH, { scope: "/" });
    } catch (err) {
      console.error("[push] Service worker registration failed:", err);
    }
  }

  async #swRegistration() {
    try {
      return await navigator.serviceWorker.ready;
    } catch {
      return null;
    }
  }

  async #syncState() {
    const reg = await this.#swRegistration();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    this.#updateUI(!!sub);
  }

  async #subscribe(registration) {
    // Guard: check current permission before asking.
    if (Notification.permission === "denied") {
      this.#showStatus(this.buttonTarget.dataset.labelDenied);
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      this.#showStatus(this.buttonTarget.dataset.labelDenied);
      return;
    }

    let sub;
    try {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.#urlB64ToUint8Array(this.vapidKeyValue),
      });
    } catch (err) {
      console.error("[push] PushManager.subscribe failed:", err);
      this.#showStatus(this.subscribeFailedMsgValue);
      return;
    }

    const subJson = sub.toJSON();
    const response = await fetch(this.subscribeUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.#csrfToken(),
      },
      body: JSON.stringify({
        push_subscription: {
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys.p256dh,
            auth: subJson.keys.auth,
          },
        },
      }),
    });

    if (response.ok) {
      this.#updateUI(true);
    } else {
      // Server rejected — roll back the browser subscription.
      await sub.unsubscribe();
      const body = await response.json().catch(() => ({}));
      this.#showStatus(body.error || this.saveFailedMsgValue);
    }
  }

  async #unsubscribe(sub) {
    // Delete from server first; if that fails, we still remove locally.
    try {
      await fetch(this.subscribeUrlValue, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.#csrfToken(),
        },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
    } catch (err) {
      console.warn("[push] Server-side unsubscribe failed:", err);
    }

    await sub.unsubscribe();
    this.#updateUI(false);
  }

  #updateUI(subscribed) {
    if (this.hasButtonTarget) {
      const btn = this.buttonTarget;
      btn.dataset.subscribed = subscribed ? "true" : "false";
      btn.classList.toggle("btn-primary", subscribed);
      btn.classList.toggle("btn-outline", !subscribed);
    }
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = subscribed
        ? this.buttonTarget.dataset.labelSubscribed || "Notifications on"
        : this.buttonTarget.dataset.labelUnsubscribed || "Notify me when live";
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "";
      this.statusTarget.hidden = true;
    }
  }

  #setUnsupported(message) {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true;
      this.buttonTarget.setAttribute("aria-disabled", "true");
    }
    this.#showStatus(message);
  }

  #showStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message;
      this.statusTarget.hidden = false;
    }
  }

  #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  // Converts a base64url string to a Uint8Array as required by
  // PushManager.subscribe({ applicationServerKey }).
  #urlB64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  }
}
